/**
 * The point-of-sale back office's route table (C8 §5.5).
 *
 * `src/router/index.ts` is shared by every module and belongs to the wiring
 * packet, so this file declares the records and the router spreads `POS_ROUTES`
 * in. Keeping them here means the paths, the permissions and the components
 * that implement them are decided in one place by whoever owns the module.
 *
 * Every `:id` segment carries the `\d+` constraint, so no literal word can ever
 * be parsed as a document id, and each family's list route is declared before
 * its detail route so the ordering says the same thing the constraint enforces.
 *
 * **The sync-conflict inbox and the ratification queue are two screens, and the
 * approvals inbox is a third.** A conflict has an *owner*, not an approver, and
 * folding it into the approvals inbox would make "approve" mean "I have
 * investigated this" — a different act with a different permission. A deferred
 * offline authorisation is a third thing again: somebody already did it, and
 * the question is whether the business ratifies or compensates. Three acts,
 * three screens, three permissions.
 *
 * Which permission guards which screen follows the API rather than the topic:
 * the day-close board is `pos.day_close.read` because that is what
 * `GET /reports/pos-day-board` declares — *posting* it needs
 * `pos.day_close.post` with step-up MFA, and the screen checks that separately.
 *
 * The same rule places the two screens added afterwards. The operator-PIN board
 * is `pos.operator.manage` because that is what `PUT /pos-operator-pin` and
 * `POST /pos-operator-pin/:userId/reset` declare — there is no read permission
 * for a PIN, because there is nothing about one that may be read. The
 * superseded-pricing report is `pos.sale.read`, not an anomaly permission: it
 * lists *sale lines*, and a reader of sales may already see every figure in it.
 *
 * The floor plan is `pos.check.read`, not `pos.outlet.manage`, for the same
 * reason: `GET /pos-floor-areas` and `GET /pos-tables` declare it, and a server
 * who may see the floor may open the screen. Creating a section, replacing an
 * area's tables and adding a station each need `pos.outlet.manage`, which the
 * screen checks separately — so the reader sees the plan and the manager edits
 * it, from one route.
 *
 * Publishing an operator roster has no route of its own. It is scoped to one
 * device — the verifier it derives works on that terminal and nowhere else —
 * so it is an action on the device page rather than a screen that would have to
 * begin by asking which device.
 */

import type { RouteRecordRaw } from 'vue-router'

import { DEVICE_PERMISSIONS, POS_PERMISSIONS } from '@/modules/pos/permissions'

export const POS_ROUTES: RouteRecordRaw[] = [
  {
    path: '/pos/outlets',
    name: 'pos-outlets',
    component: () => import('@/modules/pos/OutletsListView.vue'),
    meta: { titleKey: 'pos.routeTitles.outlets', permission: POS_PERMISSIONS.outletRead },
  },
  {
    path: '/pos/outlets/:id(\\d+)',
    name: 'pos-outlet',
    component: () => import('@/modules/pos/OutletView.vue'),
    meta: { titleKey: 'pos.routeTitles.outlet', permission: POS_PERMISSIONS.outletRead },
  },
  {
    path: '/pos/settings',
    name: 'pos-settings',
    component: () => import('@/modules/pos/PosSettingsView.vue'),
    meta: { titleKey: 'pos.routeTitles.settings', permission: POS_PERMISSIONS.outletRead },
  },
  {
    path: '/pos/devices',
    name: 'pos-devices',
    component: () => import('@/modules/pos/DeviceFleetView.vue'),
    meta: { titleKey: 'pos.routeTitles.devices', permission: DEVICE_PERMISSIONS.deviceRead },
  },
  {
    path: '/pos/devices/:id(\\d+)',
    name: 'pos-device',
    component: () => import('@/modules/pos/DeviceView.vue'),
    meta: { titleKey: 'pos.routeTitles.device', permission: DEVICE_PERMISSIONS.deviceRead },
  },
  {
    path: '/pos/content-sets',
    name: 'pos-content-sets',
    component: () => import('@/modules/pos/ContentSetsListView.vue'),
    meta: { titleKey: 'pos.routeTitles.contentSets', permission: DEVICE_PERMISSIONS.contentRead },
  },
  {
    path: '/pos/content-sets/:id(\\d+)',
    name: 'pos-content-set',
    component: () => import('@/modules/pos/ContentSetView.vue'),
    meta: { titleKey: 'pos.routeTitles.contentSet', permission: DEVICE_PERMISSIONS.contentRead },
  },
  {
    path: '/pos/menus',
    name: 'pos-menus',
    component: () => import('@/modules/pos/MenusListView.vue'),
    meta: { titleKey: 'pos.routeTitles.menus', permission: POS_PERMISSIONS.menuRead },
  },
  {
    path: '/pos/menu-versions/:id(\\d+)',
    name: 'pos-menu-version',
    component: () => import('@/modules/pos/MenuVersionView.vue'),
    meta: { titleKey: 'pos.routeTitles.menuVersion', permission: POS_PERMISSIONS.menuRead },
  },
  {
    path: '/pos/floor-plan',
    name: 'pos-floor-plan',
    component: () => import('@/modules/pos/FloorPlanView.vue'),
    meta: { titleKey: 'pos.routeTitles.floorPlan', permission: POS_PERMISSIONS.checkRead },
  },
  {
    path: '/pos/availability',
    name: 'pos-availability',
    component: () => import('@/modules/pos/AvailabilityBoardView.vue'),
    meta: { titleKey: 'pos.routeTitles.availability', permission: POS_PERMISSIONS.menuRead },
  },
  {
    path: '/pos/sales',
    name: 'pos-sales',
    component: () => import('@/modules/pos/SalesListView.vue'),
    meta: { titleKey: 'pos.routeTitles.sales', permission: POS_PERMISSIONS.saleRead },
  },
  {
    path: '/pos/sales/:id(\\d+)',
    name: 'pos-sale',
    component: () => import('@/modules/pos/SaleView.vue'),
    meta: { titleKey: 'pos.routeTitles.sale', permission: POS_PERMISSIONS.saleRead },
  },
  {
    path: '/pos/retail-ledgers',
    name: 'pos-retail-ledgers',
    component: () => import('@/modules/pos/RetailLedgersView.vue'),
    meta: { titleKey: 'pos.routeTitles.retailLedgers', permission: POS_PERMISSIONS.retailManage },
  },
  {
    path: '/pos/cash-sessions',
    name: 'pos-cash-sessions',
    component: () => import('@/modules/pos/CashSessionsView.vue'),
    meta: { titleKey: 'pos.routeTitles.cashSessions', permission: POS_PERMISSIONS.sessionRead },
  },
  {
    path: '/pos/cash-sessions/:id(\\d+)',
    name: 'pos-cash-session',
    component: () => import('@/modules/pos/CashSessionView.vue'),
    meta: { titleKey: 'pos.routeTitles.cashSession', permission: POS_PERMISSIONS.sessionRead },
  },
  {
    path: '/pos/day-close',
    name: 'pos-day-close-board',
    component: () => import('@/modules/pos/DayCloseBoardView.vue'),
    meta: { titleKey: 'pos.routeTitles.dayCloseBoard', permission: POS_PERMISSIONS.dayCloseRead },
  },
  {
    path: '/pos/day-closes/:id(\\d+)',
    name: 'pos-day-close',
    component: () => import('@/modules/pos/DayCloseView.vue'),
    meta: { titleKey: 'pos.routeTitles.dayClose', permission: POS_PERMISSIONS.dayCloseRead },
  },
  {
    path: '/pos/operator-pins',
    name: 'pos-operator-pins',
    component: () => import('@/modules/pos/OperatorPinsView.vue'),
    meta: {
      titleKey: 'pos.routeTitles.operatorPins',
      permission: POS_PERMISSIONS.operatorManage,
    },
  },
  {
    path: '/pos/anomalies',
    name: 'pos-anomalies',
    component: () => import('@/modules/pos/AnomalyBoardView.vue'),
    meta: { titleKey: 'pos.routeTitles.anomalies', permission: POS_PERMISSIONS.anomalyRead },
  },
  {
    path: '/pos/reports/superseded-pricing',
    name: 'pos-superseded-pricing',
    component: () => import('@/modules/pos/SupersededPricingView.vue'),
    meta: {
      titleKey: 'pos.routeTitles.supersededPricing',
      permission: POS_PERMISSIONS.saleRead,
    },
  },
  {
    path: '/pos/sync-conflicts',
    name: 'pos-sync-conflicts',
    component: () => import('@/modules/pos/ConflictInboxView.vue'),
    meta: { titleKey: 'pos.routeTitles.conflicts', permission: DEVICE_PERMISSIONS.syncRead },
  },
  {
    path: '/pos/ratifications',
    name: 'pos-ratifications',
    component: () => import('@/modules/pos/RatificationsView.vue'),
    meta: { titleKey: 'pos.routeTitles.ratifications', permission: DEVICE_PERMISSIONS.syncRead },
  },
]

/** The same set as strings, for links inside the module. */
export const POS_PATHS = {
  outlets: '/pos/outlets',
  outlet: (id: number) => `/pos/outlets/${id}`,
  settings: '/pos/settings',
  devices: '/pos/devices',
  device: (id: number) => `/pos/devices/${id}`,
  contentSets: '/pos/content-sets',
  contentSet: (id: number) => `/pos/content-sets/${id}`,
  menus: '/pos/menus',
  menuVersion: (id: number) => `/pos/menu-versions/${id}`,
  availability: '/pos/availability',
  floorPlan: '/pos/floor-plan',
  sales: '/pos/sales',
  sale: (id: number) => `/pos/sales/${id}`,
  retailLedgers: '/pos/retail-ledgers',
  cashSessions: '/pos/cash-sessions',
  cashSession: (id: number) => `/pos/cash-sessions/${id}`,
  dayCloseBoard: '/pos/day-close',
  dayClose: (id: number) => `/pos/day-closes/${id}`,
  operatorPins: '/pos/operator-pins',
  anomalies: '/pos/anomalies',
  supersededPricing: '/pos/reports/superseded-pricing',
  conflicts: '/pos/sync-conflicts',
  ratifications: '/pos/ratifications',
} as const

/** Deep links out of the module, to screens other packets own. */
export const POS_EXTERNAL_PATHS = {
  journalEntry: (id: number) => `/gl/journal-entries/${id}`,
  businessDays: '/operations/day-board',
} as const
